import type { Client, ClientHeaders } from "@medusajs/js-sdk";

import type { StoreStockReorderLine } from "./stock-tracking";

/**
 * Client for the **voice ordering** store endpoints (KAN-142). Three
 * endpoints:
 *
 * - `POST /store/voice-order/parse` (KAN-144, the standalone
 *   `@repo/voice-order-plugin`): a multipart audio upload, transcribed and
 *   matched against the catalog into `exact` / `ambiguous` / `not_found`
 *   items. Seller-less by design - product identity only, at an indicative
 *   Typesense-snapshot price.
 * - `POST /store/voice-order/confirm` (KAN-147, `b2c-core`): the chosen
 *   `{variant_id, quantity}` selections resolved live to a supplier + offer
 *   each, returning the same changed-line diff (`available` / `repriced` /
 *   `unavailable`, per-line `seller`) `/store/stock-tracking/reorder` returns -
 *   previewed with `dry_run: true`, then committed with `dry_run: false`,
 *   fanning out across seller carts.
 * - `POST /store/voice-order/messages` (KAN-192, the same
 *   `@repo/voice-order-plugin`): a standalone "leave a voice message"
 *   capture - a multipart audio upload, stored securely (private S3 via
 *   `Modules.FILE`) with no transcription and no catalog matching.
 *   Independent of `parse`/`confirm` - the storefront never chains it with
 *   either; retrieval is admin-only.
 *
 * `parse` and `confirm` are chained by the storefront (never by one backend
 * calling the other). All three require an authenticated customer
 * (JWT/cookie); there is no cart-id header - customer identity comes from
 * auth, and for `parse`/`confirm` the seller carts are resolved server-side.
 */

/** How a spoken item resolved against the catalog. */
export type StoreVoiceOrderMatchType = "exact" | "ambiguous" | "not_found";

/** The resolved product returned for an `exact` match. */
export interface StoreVoiceOrderResolvedProduct {
  variant_id: string;
  title: string;
  /**
   * Indicative Typesense-snapshot price (the catalog currency's major units),
   * or `null` when the document carries no price. NOT the buyer's authoritative
   * B2B price - that is resolved live at confirm time. Label it as indicative.
   */
  indicative_price: number | null;
}

/** One candidate to choose between for an `ambiguous` match (price-less: the
 *  authoritative price is resolved once the choice is made, at confirm time). */
export interface StoreVoiceOrderProductOption {
  variant_id: string;
  title: string;
}

interface StoreVoiceOrderItemBase {
  /** The product the buyer spoke, verbatim from the extraction step. */
  spoken_name: string;
  quantity: number;
}

export interface StoreVoiceOrderExactItem extends StoreVoiceOrderItemBase {
  match_type: "exact";
  resolved_product: StoreVoiceOrderResolvedProduct;
}

export interface StoreVoiceOrderAmbiguousItem extends StoreVoiceOrderItemBase {
  match_type: "ambiguous";
  options: StoreVoiceOrderProductOption[];
}

export interface StoreVoiceOrderNotFoundItem extends StoreVoiceOrderItemBase {
  match_type: "not_found";
}

export type StoreVoiceOrderItem =
  | StoreVoiceOrderExactItem
  | StoreVoiceOrderAmbiguousItem
  | StoreVoiceOrderNotFoundItem;

/** Response of `POST /store/voice-order/parse`. Seller-less by design. */
export interface StoreVoiceOrderParseResponse {
  transcript: string;
  items: StoreVoiceOrderItem[];
}

/** A buyer's final pick + quantity for the confirm call, keyed by `variant_id`. */
export interface StoreVoiceOrderConfirmSelection {
  variant_id: string;
  quantity: number;
}

export interface StoreVoiceOrderConfirmRequest {
  selections: StoreVoiceOrderConfirmSelection[];
  /**
   * Preview only (`true`) or add the resolved lines to the seller cart(s)
   * (`false`). Defaults to `true` server-side.
   */
  dry_run?: boolean;
}

export interface StoreVoiceOrderConfirmResponse {
  /**
   * The seller carts the lines were added to - empty on a `dry_run` preview
   * (and when nothing was addable), one id per seller after a commit (a single
   * spoken order can fan out across suppliers).
   */
  cart_ids: string[];
  /**
   * ISO currency (e.g. `"aud"`) the line prices are quoted in. Amounts in
   * `lines` are in this currency's major units.
   */
  currency_code: string;
  /**
   * Every selection, resolved + classified - flat, one row per selection, each
   * carrying its own resolved `seller`. Group by seller client-side for the
   * review UI (KAN-143). Identical shape to `/store/stock-tracking/reorder`.
   */
  lines: StoreStockReorderLine[];
}

/** Response of `POST /store/voice-order/messages`. No `file_id` or download
 *  URL - retrieval is admin-only by requirement (KAN-192). */
export interface StoreVoiceMessageResponse {
  id: string;
}

/** Options accepted by both calls - an abort signal (for the client-side hard
 *  timeout, KAN-143) and per-request headers. */
interface VoiceOrderRequestOptions {
  signal?: AbortSignal;
  headers?: ClientHeaders;
}

export class VoiceOrder {
  private client: Client;

  constructor(client: Client) {
    this.client = client;
  }

  /**
   * Transcribe + match a spoken order. The recorded clip is sent as
   * `multipart/form-data` in the `audio` field - binary, so it cannot go
   * through the JSON body path. Pass `options.signal` for the KAN-143 30s hard
   * client timeout so a stuck request never spins indefinitely.
   */
  parse = async (
    audio: Blob,
    options?: VoiceOrderRequestOptions & { filename?: string }
  ): Promise<StoreVoiceOrderParseResponse> => {
    const form = new FormData();
    form.append("audio", audio, options?.filename ?? "voice-order");

    return this.client.fetch<StoreVoiceOrderParseResponse>(
      "/store/voice-order/parse",
      {
        method: "POST",
        body: form,
        // Drop the SDK's default `content-type: application/json` (null deletes
        // the header) so the browser sets the multipart boundary itself;
        // otherwise the client would JSON.stringify the FormData. Any
        // caller-supplied headers still layer on top.
        headers: { "content-type": null, ...options?.headers },
        signal: options?.signal
      }
    );
  };

  /**
   * Leave a short voice message (not an order) - stored securely and
   * retrievable only via the admin panel. Independent of `parse`/`confirm`:
   * never chained with either. The recorded clip is sent the same way as
   * `parse` - `multipart/form-data` in the `audio` field.
   */
  leaveMessage = async (
    audio: Blob,
    options?: VoiceOrderRequestOptions & { filename?: string }
  ): Promise<StoreVoiceMessageResponse> => {
    const form = new FormData();
    form.append("audio", audio, options?.filename ?? "voice-message");

    return this.client.fetch<StoreVoiceMessageResponse>(
      "/store/voice-order/messages",
      {
        method: "POST",
        body: form,
        // See `parse` above - drop the SDK's default JSON content-type so the
        // browser sets the multipart boundary itself.
        headers: { "content-type": null, ...options?.headers },
        signal: options?.signal
      }
    );
  };

  /**
   * Resolve the chosen selections to live seller offers. `dryRun: true`
   * previews the changed-line diff without mutating any cart (the KAN-143
   * seller-grouped review); `dryRun: false` adds the available + repriced lines
   * to the appropriate seller cart(s) and returns the affected `cart_ids`.
   */
  confirm = async (
    selections: StoreVoiceOrderConfirmSelection[],
    dryRun: boolean,
    options?: VoiceOrderRequestOptions
  ): Promise<StoreVoiceOrderConfirmResponse> =>
    this.client.fetch<StoreVoiceOrderConfirmResponse>(
      "/store/voice-order/confirm",
      {
        method: "POST",
        body: {
          selections,
          dry_run: dryRun
        } satisfies StoreVoiceOrderConfirmRequest,
        headers: options?.headers,
        signal: options?.signal
      }
    );
}
